/**
 * 数字滚动混合器 - 完整修改版（每次经过都重新加载动画）
 * 不依赖全局注册
 */

// 默认配置
const defaultConfig = {
  duration: 2000,
  easing: 'easeOut',
  delay: 0,
  separator: '',
  prefix: '',
  suffix: '',
  once: false, // 改为false，允许多次触发
  threshold: 0.1,
  resetOnReenter: true, // 新增：重新进入时是否重置动画
  resetDuration: 0 // 新增：重置动画的持续时间
};

// 缓动函数
const easingFunctions = {
  linear: t => t,
  easeIn: t => t * t,
  easeOut: t => t * (2 - t),
  easeInOut: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
  bounce: t => {
    if (t < 1 / 2.75) return 7.5625 * t * t;
    if (t < 2 / 2.75) { t -= 1.5 / 2.75; return 7.5625 * t * t + 0.75; }
    if (t < 2.5 / 2.75) { t -= 2.25 / 2.75; return 7.5625 * t * t + 0.9375; }
    t -= 2.625 / 2.75;
    return 7.5625 * t * t + 0.984375;
  }
};

// 格式化数字
function formatNumberWithSeparator(number, separator) {
  if (!separator) return number.toString();
  const parts = number.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return parts.join('.');
}

// 重置数字到初始状态
function resetToInitialState(el, config) {
  return new Promise(resolve => {
    if (config.resetDuration > 0) {
      // 有动画的复位
      const startTime = Date.now();
      const currentText = el.textContent.replace(config.prefix, '').replace(config.suffix, '');
      const currentNumber = parseFloat(currentText.replace(new RegExp(config.separator, 'g'), '')) || 0;
      
      function update() {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / config.resetDuration, 1);
        
        let currentValue = currentNumber * (1 - progress);
        let displayNumber = currentValue.toString();
        
        if (config.separator) {
          displayNumber = formatNumberWithSeparator(Math.floor(currentValue), config.separator);
        }
        
        el.textContent = config.prefix + displayNumber + config.suffix;
        
        if (progress < 1) {
          requestAnimationFrame(update);
        } else {
          el.textContent = config.prefix + '0' + config.suffix;
          resolve();
        }
      }
      
      requestAnimationFrame(update);
    } else {
      // 立即复位
      el.textContent = config.prefix + '0' + config.suffix;
      resolve();
    }
  });
}

// 动画函数
export function animateNumber(el, targetNumber, config = {}) {
  if (!el) return;
  
  const mergedConfig = { ...defaultConfig, ...config };
  
  // 设置动画状态
  el.dataset.isAnimated = 'true';
  
  const startTime = Date.now();
  const duration = mergedConfig.duration;
  const easingFn = easingFunctions[mergedConfig.easing] || easingFunctions.easeOut;
  const hasDecimal = targetNumber % 1 !== 0;
  const decimalPlaces = hasDecimal ? targetNumber.toString().split('.')[1]?.length || 0 : 0;
  
  // 更新动画计数器
  const currentCount = parseInt(el.dataset.animationCount || '0');
  el.dataset.animationCount = (currentCount + 1).toString();
  
  function update() {
    const currentTime = Date.now();
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easingFn(progress);
    
    let currentNumber;
    if (hasDecimal) {
      currentNumber = easedProgress * targetNumber;
      currentNumber = parseFloat(currentNumber.toFixed(decimalPlaces));
    } else {
      currentNumber = Math.floor(easedProgress * targetNumber);
    }
    
    // 格式化显示
    let displayNumber = currentNumber.toString();
    if (mergedConfig.separator && !hasDecimal) {
      displayNumber = formatNumberWithSeparator(currentNumber, mergedConfig.separator);
    }
    
    el.textContent = mergedConfig.prefix + displayNumber + mergedConfig.suffix;
    
    if (progress < 1) {
      requestAnimationFrame(update);
    } else {
      // 动画完成
      let finalDisplay = targetNumber.toString();
      if (mergedConfig.separator && !hasDecimal) {
        finalDisplay = formatNumberWithSeparator(targetNumber, mergedConfig.separator);
      }
      el.textContent = mergedConfig.prefix + finalDisplay + mergedConfig.suffix;
      
      // 标记动画完成
      setTimeout(() => {
        el.dataset.isAnimated = 'false';
      }, 100);
      
      // 触发完成事件
      const event = new CustomEvent('number-scroll-complete', {
        detail: { 
          targetNumber, 
          element: el,
          animationCount: el.dataset.animationCount
        }
      });
      el.dispatchEvent(event);
    }
  }
  
  requestAnimationFrame(update);
}

// 创建局部指令
export function createNumberScrollDirective(config = {}) {
  const localConfig = { ...defaultConfig, ...config };
  
  return {
    bind(el, binding) {
      // 获取目标数字
      let targetNumber;
      let elementConfig = { ...localConfig };
      
      if (typeof binding.value === 'number') {
        targetNumber = binding.value;
      } else if (typeof binding.value === 'object') {
        targetNumber = binding.value.number || parseFloat(binding.value) || 0;
        elementConfig = { ...elementConfig, ...binding.value };
      } else {
        targetNumber = parseFloat(binding.value);
      }
      
      if (isNaN(targetNumber) || targetNumber <= 0) {
        console.warn('v-number-scroll: 必须提供有效的正数');
        return;
      }
      
      // 保存数据到元素
      el.dataset.originalNumber = targetNumber.toString();
      el.dataset.isAnimated = 'false';
      el.dataset.scrollConfig = JSON.stringify(elementConfig);
      el.dataset.animationCount = '0';
      
      // 设置初始显示
      el.textContent = elementConfig.prefix + '0' + elementConfig.suffix;
    },
    
    inserted(el) {
      const targetNumber = parseFloat(el.dataset.originalNumber);
      const elementConfig = JSON.parse(el.dataset.scrollConfig || '{}');
      
      // 创建 Intersection Observer
      const observer = new IntersectionObserver(
        async (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              // 每次进入视口都触发
              if (elementConfig.resetOnReenter && el.dataset.isAnimated === 'false') {
                // 如果需要重置且有重置动画，先执行重置
                await resetToInitialState(el, elementConfig);
              }
              
              // 等待动画状态可用
              if (el.dataset.isAnimated === 'false') {
                setTimeout(() => {
                  animateNumber(el, targetNumber, elementConfig);
                }, elementConfig.delay);
              }
              
              // 如果once为true，只观察一次
              if (elementConfig.once) {
                observer.unobserve(el);
              }
            } else {
              // 离开视口时，如果不执行once模式，则标记为可重新动画
              if (!elementConfig.once && elementConfig.resetOnReenter) {
                // 延迟标记，避免动画未完成就被重置
                setTimeout(() => {
                  if (el.dataset.isAnimated === 'true') {
                    el.dataset.isAnimated = 'pending-reset';
                  }
                }, 100);
              }
            }
          }
        },
        {
          threshold: elementConfig.threshold,
          rootMargin: '0px 0px -50px 0px'
        }
      );
      
      observer.observe(el);
      el._numberScrollObserver = observer;
    },
    
    componentUpdated(el, binding) {
      // 如果值发生变化，重新设置
      const oldValue = el.dataset.originalNumber;
      let newValue;
      let newConfig = { ...localConfig };
      
      if (typeof binding.value === 'number') {
        newValue = binding.value;
      } else if (typeof binding.value === 'object') {
        newValue = binding.value.number || parseFloat(binding.value) || 0;
        newConfig = { ...newConfig, ...binding.value };
      } else {
        newValue = parseFloat(binding.value);
      }
      
      if (oldValue !== newValue.toString() && !isNaN(newValue) && newValue > 0) {
        // 移除旧的观察器
        if (el._numberScrollObserver) {
          el._numberScrollObserver.disconnect();
          delete el._numberScrollObserver;
        }
        
        // 重置元素数据
        el.dataset.originalNumber = newValue.toString();
        el.dataset.isAnimated = 'false';
        el.dataset.scrollConfig = JSON.stringify(newConfig);
        el.dataset.animationCount = '0';
        el.textContent = newConfig.prefix + '0' + newConfig.suffix;
        
        // 重新观察
        const observer = new IntersectionObserver(
          async (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                if (newConfig.resetOnReenter && el.dataset.isAnimated === 'false') {
                  await resetToInitialState(el, newConfig);
                }
                
                if (el.dataset.isAnimated === 'false') {
                  setTimeout(() => {
                    animateNumber(el, newValue, newConfig);
                  }, newConfig.delay);
                }
                
                if (newConfig.once) {
                  observer.unobserve(el);
                }
              } else {
                if (!newConfig.once && newConfig.resetOnReenter) {
                  setTimeout(() => {
                    if (el.dataset.isAnimated === 'true') {
                      el.dataset.isAnimated = 'pending-reset';
                    }
                  }, 100);
                }
              }
            }
          },
          {
            threshold: newConfig.threshold,
            rootMargin: '0px 0px -50px 0px'
          }
        );
        
        observer.observe(el);
        el._numberScrollObserver = observer;
      }
    },
    
    unbind(el) {
      if (el._numberScrollObserver) {
        el._numberScrollObserver.disconnect();
        delete el._numberScrollObserver;
      }
    }
  };
}

// 局部方法
export const numberScrollMethods = {
  // 手动触发数字滚动
  async trigger(element, targetNumber, options = {}) {
    let el;
    if (typeof element === 'string') {
      el = document.querySelector(element);
    } else {
      el = element;
    }
    
    if (!el) {
      console.warn('未找到目标元素');
      return;
    }
    
    if (isNaN(targetNumber) || targetNumber <= 0) {
      console.warn('必须提供有效的正数');
      return;
    }
    
    const config = { ...defaultConfig, ...options };
    
    // 重置到初始状态
    if (config.resetOnReenter) {
      await resetToInitialState(el, config);
    }
    
    animateNumber(el, targetNumber, config);
  },
  
  // 重置元素
  async reset(element) {
    let el;
    if (typeof element === 'string') {
      el = document.querySelector(element);
    } else {
      el = element;
    }
    
    if (!el) return;
    
    const config = JSON.parse(el.dataset.scrollConfig || '{}');
    el.dataset.isAnimated = 'false';
    el.dataset.animationCount = '0';
    
    await resetToInitialState(el, config);
    
    // 重新观察
    if (el._numberScrollObserver) {
      el._numberScrollObserver.disconnect();
      delete el._numberScrollObserver;
    }
    
    const observer = new IntersectionObserver(
      async (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const targetNumber = parseFloat(el.dataset.originalNumber);
            
            if (config.resetOnReenter && el.dataset.isAnimated === 'false') {
              await resetToInitialState(el, config);
            }
            
            if (el.dataset.isAnimated === 'false') {
              setTimeout(() => {
                animateNumber(el, targetNumber, config);
              }, config.delay);
            }
            
            if (config.once) {
              observer.unobserve(el);
            }
          }
        }
      },
      {
        threshold: config.threshold,
        rootMargin: '0px 0px -50px 0px'
      }
    );
    
    observer.observe(el);
    el._numberScrollObserver = observer;
  },
  
  // 重置所有
  resetAll() {
    document.querySelectorAll('[v-number-scroll]').forEach(el => {
      this.reset(el);
    });
  },
  
  // 立即执行所有可见元素的动画
  triggerVisible() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const targetNumber = parseFloat(el.dataset.originalNumber);
            const config = JSON.parse(el.dataset.scrollConfig || '{}');
            
            if (el.dataset.isAnimated === 'false') {
              animateNumber(el, targetNumber, config);
            }
          }
        });
      },
      { threshold: 0.1 }
    );
    
    document.querySelectorAll('[v-number-scroll]').forEach(el => {
      observer.observe(el);
    });
  },
  
  // 停止所有动画
  stopAll() {
    document.querySelectorAll('[v-number-scroll]').forEach(el => {
      el.dataset.isAnimated = 'stopped';
    });
  },
  
  // 获取动画统计信息
  getStats() {
    const elements = document.querySelectorAll('[v-number-scroll]');
    return {
      total: elements.length,
      animated: Array.from(elements).filter(el => el.dataset.isAnimated === 'true').length,
      pending: Array.from(elements).filter(el => el.dataset.isAnimated === 'false').length,
      animationCounts: Array.from(elements).map(el => ({
        element: el,
        count: parseInt(el.dataset.animationCount || '0'),
        isAnimated: el.dataset.isAnimated
      }))
    };
  }
};

// 导出所有内容
export default {
  easingFunctions,
  animateNumber,
  createNumberScrollDirective,
  numberScrollMethods,
  resetToInitialState
};